/**
 * Communication Preferences card (Phase 6).
 *
 * Renders on the contact detail page. Shows:
 *   - 5 status badges (email marketing, SMS marketing, operational email/SMS, DNC)
 *   - Consent + unsubscribe metadata
 *   - Manual staff actions: Unsubscribe, Set DNC, Resubscribe (admin only), Clear DNC (admin only)
 *   - Full preference-history timeline
 */
import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  ShieldAlert,
  ShieldCheck,
  Ban,
  MessageCircleOff,
  MailX,
  History,
  Loader2,
  Check,
  X,
  Info,
  PauseCircle,
} from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
  contactsApi,
  CONSENT_SOURCES,
  type ConsentSource,
  type PreferenceAction,
  type PreferenceHistoryRow,
  type EmailMarketingStatus,
  type SmsMarketingStatus,
  type OperationalStatus,
  ApiError,
} from "@/lib/api"
import { formatDateTime, timeAgo } from "@shared/utils/date"
import { cn } from "@/lib/utils"
import { useAuth } from "@/hooks/useAuth"

// ────────────────────────────────────────────────────────────────
// Status → colour helpers
// ────────────────────────────────────────────────────────────────

function emailMarketingTone(s: EmailMarketingStatus): string {
  switch (s) {
    case "subscribed":
      return "bg-emerald-100 text-emerald-700"
    case "unsubscribed":
      return "bg-rose-100 text-rose-700"
    case "bounced":
    case "complained":
      return "bg-amber-100 text-amber-700"
    default:
      return "bg-rc-grey-100 text-rc-grey-700"
  }
}

function smsMarketingTone(s: SmsMarketingStatus): string {
  switch (s) {
    case "subscribed":
      return "bg-emerald-100 text-emerald-700"
    case "unsubscribed":
      return "bg-rose-100 text-rose-700"
    case "invalid":
    case "complained":
      return "bg-amber-100 text-amber-700"
    default:
      return "bg-rc-grey-100 text-rc-grey-700"
  }
}

function operationalTone(s: OperationalStatus): string {
  return s === "restricted"
    ? "bg-rose-100 text-rose-700"
    : "bg-emerald-100 text-emerald-700"
}

const STATUS_LABELS: Record<string, string> = {
  subscribed: "Subscribed",
  unsubscribed: "Unsubscribed",
  bounced: "Bounced",
  complained: "Complained",
  invalid: "Invalid",
  unknown: "Unknown",
  allowed: "Allowed",
  restricted: "Restricted",
}

// ────────────────────────────────────────────────────────────────
// Human-friendly change source labels
// ────────────────────────────────────────────────────────────────

const CHANGE_SOURCE_LABELS: Record<string, string> = {
  sms_stop_reply: "Client texted STOP",
  sms_dnc_reply: "Client requested no contact",
  email_unsubscribe_link: "Unsubscribe link",
  preferences_page: "Preferences page",
  "staff manual update": "Staff manual update",
  import: "Import",
  system_update: "System update",
  bulk_import: "Bulk import",
}

const FIELD_LABELS: Record<string, string> = {
  email_marketing_status: "Email marketing",
  sms_marketing_status: "SMS marketing",
  operational_email_status: "Operational email",
  operational_sms_status: "Operational SMS",
  do_not_contact_status: "Do not contact",
}

// ────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────

export function CommunicationPreferencesCard({ contactId }: { contactId: string }) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const canResubscribe =
    user?.role === "admin" || user?.role === "manager"

  const { data, isLoading, error } = useQuery({
    queryKey: ["contact-preferences", contactId],
    queryFn: () => contactsApi.getPreferences(contactId),
  })

  const [flash, setFlash] = useState<string | null>(null)
  const mutate = useMutation({
    mutationFn: (input: Parameters<typeof contactsApi.updatePreferences>[1]) =>
      contactsApi.updatePreferences(contactId, input),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["contact-preferences", contactId] })
      qc.invalidateQueries({ queryKey: ["contact", contactId] })
      qc.invalidateQueries({ queryKey: ["automation-runs"] })
      if (r.cancelledRunCount && r.cancelledRunCount > 0) {
        setFlash(
          `Preferences updated. Stopped ${r.cancelledRunCount} active automation${
            r.cancelledRunCount === 1 ? "" : "s"
          } for this contact.`
        )
      } else if (r.changed) {
        setFlash("Preferences updated.")
      }
      window.setTimeout(() => setFlash(null), 8000)
    },
  })

  const [actionOpen, setActionOpen] = useState<null | {
    action: PreferenceAction
    title: string
    description: string
    channel?: "email" | "sms" | "all"
    requiresConsent?: boolean
  }>(null)

  if (isLoading) {
    return (
      <Card className="p-5">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading preferences…
        </div>
      </Card>
    )
  }
  if (error || !data) {
    return (
      <Card className="p-5">
        <p className="text-sm text-rose-600">
          Couldn't load communication preferences.
        </p>
      </Card>
    )
  }

  const p = data.preferences
  const now = Math.floor(Date.now() / 1000)
  const isPaused =
    p.automationPausedUntil != null && p.automationPausedUntil > now
  const pauseDaysLeft = isPaused
    ? Math.ceil(((p.automationPausedUntil ?? 0) - now) / 86400)
    : 0

  return (
    <div className="space-y-6">
      {flash && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          {flash}
        </div>
      )}

      {isPaused && (
        <Card className="border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <PauseCircle className="mt-0.5 h-5 w-5 flex-none text-amber-700" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900">
                Automations paused for {pauseDaysLeft} more day
                {pauseDaysLeft === 1 ? "" : "s"}
              </p>
              <p className="text-sm text-amber-900/90">
                This contact was bulk-imported
                {p.importBatchId ? ` (batch ${p.importBatchId})` : ""}. No automation
                will fire on them until the quiet period ends or you clear it below.
                Their marketing preferences still apply as normal.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setActionOpen({
                  action: "clear_automation_pause",
                  title: "Clear automation pause",
                  description:
                    "Removes the bulk-import quiet period on this contact. They'll become eligible for automation enrolments again immediately (existing marketing preferences still apply).",
                })
              }
            >
              Clear pause
            </Button>
          </div>
        </Card>
      )}
      {/* ────────── Header + Do-Not-Contact banner ────────── */}
      {p.doNotContactStatus && (
        <Card className="border-rose-200 bg-rose-50 p-4">
          <div className="flex items-start gap-3">
            <Ban className="mt-0.5 h-5 w-5 flex-none text-rose-700" />
            <div className="flex-1">
              <p className="font-semibold text-rose-900">Do Not Contact</p>
              <p className="text-sm text-rose-800">
                This contact has asked us not to reach out.
                {p.unsubscribeReason ? ` Reason: ${p.unsubscribeReason}.` : ""}
              </p>
            </div>
            {canResubscribe && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setActionOpen({
                    action: "clear_dnc",
                    title: "Clear do-not-contact flag",
                    description:
                      "This removes the DNC flag but does NOT resubscribe them to marketing. Use the Resubscribe action separately.",
                  })
                }
              >
                Clear DNC
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* ────────── Status grid ────────── */}
      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">
            Communication Preferences
          </h3>
          {p.communicationPreferencesUpdatedAt && (
            <span className="text-xs text-muted-foreground">
              Last updated {timeAgo(p.communicationPreferencesUpdatedAt)}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <PrefRow
            icon={<MailX className="h-4 w-4" />}
            label="Email marketing"
            value={STATUS_LABELS[p.emailMarketingStatus] ?? p.emailMarketingStatus}
            tone={emailMarketingTone(p.emailMarketingStatus)}
            meta={
              p.emailMarketingStatus === "unsubscribed"
                ? p.emailUnsubscribedDate
                  ? `Unsubscribed ${formatDateTime(p.emailUnsubscribedDate)}`
                  : null
                : p.emailConsentSource
                ? `Consent: ${p.emailConsentSource}${
                    p.emailConsentDate ? ` · ${formatDateTime(p.emailConsentDate)}` : ""
                  }`
                : null
            }
          />
          <PrefRow
            icon={<MessageCircleOff className="h-4 w-4" />}
            label="SMS marketing"
            value={STATUS_LABELS[p.smsMarketingStatus] ?? p.smsMarketingStatus}
            tone={smsMarketingTone(p.smsMarketingStatus)}
            meta={
              p.smsMarketingStatus === "unsubscribed"
                ? p.smsUnsubscribedDate
                  ? `Unsubscribed ${formatDateTime(p.smsUnsubscribedDate)}`
                  : null
                : p.smsConsentSource
                ? `Consent: ${p.smsConsentSource}${
                    p.smsConsentDate ? ` · ${formatDateTime(p.smsConsentDate)}` : ""
                  }`
                : null
            }
          />
          <PrefRow
            icon={<ShieldCheck className="h-4 w-4" />}
            label="Operational email"
            value={STATUS_LABELS[p.operationalEmailStatus] ?? p.operationalEmailStatus}
            tone={operationalTone(p.operationalEmailStatus)}
            meta={
              p.operationalEmailStatus === "restricted"
                ? "System notices are blocked"
                : "Settlement / update messages allowed"
            }
          />
          <PrefRow
            icon={<ShieldCheck className="h-4 w-4" />}
            label="Operational SMS"
            value={STATUS_LABELS[p.operationalSmsStatus] ?? p.operationalSmsStatus}
            tone={operationalTone(p.operationalSmsStatus)}
            meta={
              p.operationalSmsStatus === "restricted"
                ? "System notices are blocked"
                : "Settlement / update messages allowed"
            }
          />
        </div>

        {/* ────────── Manual staff actions ────────── */}
        <div className="mt-6 border-t border-border pt-4">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setActionOpen({
                  action: "unsubscribe_marketing",
                  channel: "all",
                  title: "Unsubscribe from all marketing",
                  description:
                    "Sets email + SMS marketing to Unsubscribed. Operational messages (settlement updates, etc.) still allowed.",
                })
              }
            >
              <MailX className="mr-1.5 h-3.5 w-3.5" />
              Unsubscribe all marketing
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setActionOpen({
                  action: "set_dnc",
                  title: "Set do-not-contact",
                  description:
                    "Blocks ALL communication — marketing AND operational, email AND SMS. Use only when the client has explicitly asked us to stop reaching out entirely.",
                })
              }
            >
              <Ban className="mr-1.5 h-3.5 w-3.5" />
              Set do-not-contact
            </Button>
            {canResubscribe && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setActionOpen({
                    action: "resubscribe_marketing",
                    channel: "all",
                    requiresConsent: true,
                    title: "Resubscribe to marketing",
                    description:
                      "You must record how the client re-consented. This action is audited.",
                  })
                }
                disabled={
                  p.emailMarketingStatus === "subscribed" &&
                  p.smsMarketingStatus === "subscribed"
                }
              >
                <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />
                Resubscribe
              </Button>
            )}
          </div>
          {!canResubscribe && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Info className="h-3 w-3" />
              Only admins and managers can resubscribe a contact.
            </p>
          )}
        </div>
      </Card>

      {/* ────────── History timeline ────────── */}
      <Card className="p-5">
        <div className="mb-4 flex items-center gap-2">
          <History className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">
            Preference history
          </h3>
          <span className="text-xs text-muted-foreground">
            ({data.history.length})
          </span>
        </div>
        {data.history.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No preference changes recorded yet.
          </p>
        ) : (
          <ul className="space-y-3">
            {data.history.map((row) => (
              <HistoryRow key={row.id} row={row} />
            ))}
          </ul>
        )}
      </Card>

      {/* ────────── Action dialog ────────── */}
      {actionOpen && (
        <PreferenceActionDialog
          open={!!actionOpen}
          onOpenChange={(v) => !v && setActionOpen(null)}
          action={actionOpen.action}
          channel={actionOpen.channel}
          requiresConsent={actionOpen.requiresConsent}
          title={actionOpen.title}
          description={actionOpen.description}
          isSubmitting={mutate.isPending}
          error={mutate.error instanceof ApiError ? mutate.error.message : null}
          onSubmit={async (payload) => {
            await mutate.mutateAsync(payload)
            setActionOpen(null)
            mutate.reset()
          }}
        />
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────

function PrefRow({
  icon,
  label,
  value,
  tone,
  meta,
}: {
  icon: React.ReactNode
  label: string
  value: string
  tone: string
  meta: string | null
}) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <span className="text-muted-foreground">{icon}</span>
          {label}
        </div>
        <Badge className={cn("border-transparent text-xs", tone)}>
          {value}
        </Badge>
      </div>
      {meta && (
        <p className="mt-1.5 text-xs text-muted-foreground">{meta}</p>
      )}
    </div>
  )
}

function HistoryRow({ row }: { row: PreferenceHistoryRow }) {
  const fieldLabel =
    FIELD_LABELS[row.fieldName] ?? row.fieldName.replace(/_/g, " ")
  const sourceLabel =
    CHANGE_SOURCE_LABELS[row.changeSource] ?? row.changeSource
  const prev = renderValue(row.fieldName, row.previousValue)
  const next = renderValue(row.fieldName, row.newValue)

  return (
    <li className="rounded-md border border-border bg-rc-grey-50/50 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium text-foreground">{fieldLabel}:</span>
            <span className="text-muted-foreground">{prev}</span>
            <span className="text-muted-foreground">→</span>
            <span className="font-medium text-foreground">{next}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {sourceLabel}
            {row.changedByName ? ` · by ${row.changedByName}` : ""}
            {row.changeReason ? ` · "${row.changeReason}"` : ""}
          </p>
          {row.inboundMessageText && (
            <p className="mt-1 rounded bg-white px-2 py-1 text-xs italic text-muted-foreground">
              Client texted: "{row.inboundMessageText}"
            </p>
          )}
        </div>
        <span className="flex-none text-xs text-muted-foreground">
          {timeAgo(row.changedAt)}
        </span>
      </div>
    </li>
  )
}

function renderValue(fieldName: string, value: string | null): string {
  if (value == null) return "—"
  if (fieldName === "do_not_contact_status") {
    return value === "1" ? "On" : "Off"
  }
  return STATUS_LABELS[value] ?? value
}

// ────────────────────────────────────────────────────────────────
// Preference-action dialog
// ────────────────────────────────────────────────────────────────

function PreferenceActionDialog({
  open,
  onOpenChange,
  action,
  channel,
  requiresConsent,
  title,
  description,
  isSubmitting,
  error,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  action: PreferenceAction
  channel?: "email" | "sms" | "all"
  requiresConsent?: boolean
  title: string
  description: string
  isSubmitting: boolean
  error: string | null
  onSubmit: (payload: {
    action: PreferenceAction
    channel?: "email" | "sms" | "all"
    reason: string
    consentSource?: ConsentSource
  }) => Promise<void>
}) {
  const [reason, setReason] = useState("")
  const [consentSource, setConsentSource] = useState<ConsentSource | "">("")

  const canSubmit =
    reason.trim().length > 0 &&
    (!requiresConsent || !!consentSource)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{description}</p>

          <div>
            <Label htmlFor="pref-reason" className="mb-1.5 block">
              Reason / note <span className="text-rose-600">*</span>
            </Label>
            <Textarea
              id="pref-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Client emailed at 10:34 asking to be removed."
              rows={3}
              maxLength={500}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Required for compliance. Stored in the audit trail.
            </p>
          </div>

          {requiresConsent && (
            <div>
              <Label htmlFor="pref-consent" className="mb-1.5 block">
                Consent source <span className="text-rose-600">*</span>
              </Label>
              <select
                id="pref-consent"
                value={consentSource}
                onChange={(e) => setConsentSource(e.target.value as ConsentSource)}
                className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select how they re-consented…</option>
                {CONSENT_SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              <ShieldAlert className="mr-1 inline-block h-4 w-4" />
              {error}
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            <X className="mr-1.5 h-4 w-4" />
            Cancel
          </Button>
          <Button
            onClick={() =>
              onSubmit({
                action,
                channel,
                reason: reason.trim(),
                consentSource: (consentSource || undefined) as ConsentSource | undefined,
              })
            }
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-1.5 h-4 w-4" />
            )}
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
